import { CopyAllButtonByExcelGenerator } from 'components/shared/CopyAllButton/CopyAllButton';
import { formatGridToClipboard } from 'components/shared/ReadOnlyGrid/GridUtils';
import { BasicGenericGrid } from 'components/shared/Sensitivity/Greeks/basicGenericTable';
import type { IntlKey } from 'locales';
import type { AnalyticCategoryActions, WorkspaceAnalyticCategory } from 'models';
import { FC, useCallback, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';
import type { AssetClass } from 'state/blotters/priceImprovement/priceImprovementBlotter.model';
import type { GreeksTable } from 'state/quotes/details/details.model';

interface Props {
  className?: string;
  assetClass?: AssetClass;
  data: GreeksTable;
  precision: number;

  titleKey?: IntlKey;
  noGreeksMessageKey?: IntlKey;
  copyActionName: AnalyticCategoryActions[WorkspaceAnalyticCategory];
}

export const Greeks: FC<Props> = ({
  assetClass,
  data,
  precision,
  className,
  noGreeksMessageKey,
  titleKey,
  copyActionName,
}) => {
  const generateFormattedGrid = useCallback(() => formatGridToClipboard(data), [data]);
  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);
  if (data === undefined && !!noGreeksMessageKey) {
    return <FormattedMessage id={noGreeksMessageKey} />;
  }
  return (
    <div className={className} data-e2e="greeks-card">
      {titleKey && (
        <h5 className="card-title py-2 text-secondary" data-e2e="greeks-card-title">
          <FormattedMessage id={titleKey} />
        </h5>
      )}
      <div className="table-responsive">
        <GreeksGrid assetClass={assetClass} data={data} precision={precision} />
      </div>

      <CopyAllButtonByExcelGenerator
        className="float-end"
        generateExcelGrid={generateFormattedGrid}
        analyticsCategory={logAnalyticsCategory}
        analyticsAction={copyActionName}
      />
    </div>
  );
};

const GreeksGrid: FC<Omit<Props, 'copyActionName'>> = ({ assetClass, data, precision }) => {
  const displayHeader = assetClass === undefined ? data?.columns.length !== 2 : assetClass === 'Equity';
  const displayMultiColumn = assetClass !== 'Equity';
  const tableClassName = displayHeader ? 'bg-lvl1 table-striped' : '';
  return (
    <BasicGenericGrid
      data={data}
      precision={precision}
      tableClassName={tableClassName}
      displayHeader={displayHeader}
      displayMultiColumn={displayMultiColumn}
      e2e="equity-greeks-table"
    />
  );
};
